import React, { lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ModalBase from '@/components/base/ModalBase';
import SurveyUser from '@/features/landing/SurveyUser';
import { MODAL } from '@/common/constants';
import { openModal } from '@/store/slices/modalSlice';
import { useAppDispatch, useAppSelector } from '@/hooks';
import AIFeatureGuard from '@/components/common/AIFeatureGuard';
import ProtectedRoutes from '@/routes/ProtectedRoutes';
import dashboardService from '@/services/dashboard/dashboardService';

//lazy loading
const loadLayout = () => import('@/components/layouts/Layout');
const loadStudentLayout = () => import('@/components/layouts/StudentLayout');
const loadStudentDashboard = () => import('@/features/student/StudentDashboard');
const Layout = lazy(loadLayout);

const StudentDashboard = lazy(loadStudentDashboard);
const StudentExercise = lazy(() => import('@/features/student/StudentExercise'));
const StudentExerciseSpeaking = lazy(() => import('@/features/student/StudentExerciseSpeaking'));
const StudentVocabulary = lazy(() => import('@/features/student/StudentVocabulary'));
const StudentProgress = lazy(() => import('@/features/student/StudentProgress'));
const StudentLayout = lazy(loadStudentLayout);
const StudentLearning = lazy(() => import('@/features/student/StudentLearning'));
const FlashcardPage = lazy(() => import('@/features/student/StudentVocabulary/FlashcardPage'));
const StudentSpeaking = lazy(() => import('@/features/student/StudentSpeaking'));
const StudentReading = lazy(() => import('@/features/student/StudentReading'));
const ReadingPracticeInterface = lazy(
    () => import('@/features/student/StudentReading/components/ReadingPracticeInterface'),
);
const StudentWriting = lazy(() => import('@/features/student/StudentWriting'));
const WritingPracticeInterface = lazy(
    () => import('@/features/student/StudentWriting/components/WritingPracticeInterface'),
);
const StudentListening = lazy(() => import('@/features/student/student-listening/components/StudentListening'));
const StudentGrammar = lazy(() => import('@/features/student/student-grammar/components/StudentGrammar'));
const StudentLesson = lazy(() => import('@/features/student/StudentLesson'));
const Assessments = lazy(() => import('@/features/student/Assessments'));
const StudentAIConversation = lazy(() => import('@/features/student/StudentAIConversation'));
const MobileLessons = lazy(() => import('@/components/mobile/MobileLessons'));
const MobileProfileWrapper = lazy(() => import('@/components/mobile/MobileProfileWrapper'));
const MobileAchievementsWrapper = lazy(() => import('@/components/mobile/MobileAchievementsWrapper'));
const MobileAIChat = lazy(() => import('@/mobile/student/AIChat'));
const MobileProfileSettings = lazy(() => import('@/mobile/student/profile/settings'));
const MobilePersonalInfo = lazy(() => import('@/mobile/student/profile/personalInfo'));
const MobileLearningObjectives = lazy(() => import('@/mobile/student/profile/learningObjectives'));
const NotFoundPage = lazy(() => import('@/components/common/NotFoundPage'));

let preloaded = false;
// Nested lazy routes load one after another (Layout -> StudentLayout -> page, each only once its
// parent renders). Start them together, and on the dashboard also start its API call, so the data
// request overlaps the chunk downloads instead of waiting for them.
const preloadStudentRoute = (pathname: string) => {
    if (preloaded) return;
    preloaded = true;
    loadLayout();
    loadStudentLayout();
    if (/^\/student\/?(dashboard\/?)?$/.test(pathname)) {
        loadStudentDashboard();
        dashboardService.prefetchDashboardData();
    }
};

const StudentRoutes: React.FC = () => {
    const isModalOpen = useAppSelector((state) => state.modal.isModalOpen);
    const { profileCompleted, isInitialAssessmentCompleted } = useAppSelector((state) => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    preloadStudentRoute(location.pathname);

    useEffect(() => {
        if (!profileCompleted) {
            dispatch(openModal(MODAL.SURVEY));
            // dispatch(setHiddenPopupSurvey(true));
        } else if (!isInitialAssessmentCompleted) {
            // navigate('/testing');
        }
    }, [profileCompleted]);

    return (
        <>
            <ModalBase open={isModalOpen[MODAL.SURVEY]}>
                <SurveyUser />
            </ModalBase>
            <ProtectedRoutes>
                <Layout>
                    {' '}
                    <Routes>
                        {/* Learning routes - fullscreen learning interface */}
                        <Route path="/learning" element={<StudentLearning />} />
                        <Route path="/learning/:skill" element={<StudentLearning />} />

                        {/* Other routes - use StudentLayout */}
                        <Route
                            path="/*"
                            element={
                                <StudentLayout>
                                    <Routes>
                                        <Route path="/dashboard" element={<StudentDashboard />} />
                                        <Route
                                            path="/conversation"
                                            element={
                                                <AIFeatureGuard feature="conversation">
                                                    <StudentAIConversation />
                                                </AIFeatureGuard>
                                            }
                                        />
                                        <Route
                                            path="/ai-avatar-chat"
                                            element={
                                                <AIFeatureGuard feature="conversation">
                                                    <MobileAIChat />
                                                </AIFeatureGuard>
                                            }
                                        />
                                        <Route path="/lessons" element={<MobileLessons />} />
                                        <Route path="/vocabulary/flashcard" element={<FlashcardPage />} />
                                        <Route path="/vocabulary" element={<StudentLesson type="vocabulary" />} />
                                        <Route path="/speaking" element={<StudentLesson type="speaking" />} />
                                        <Route path="/listening" element={<StudentLesson type="listening" />} />
                                        <Route path="/grammar" element={<StudentLesson type="grammar" />} />
                                        <Route path="/reading" element={<StudentLesson type="reading" />} />
                                        <Route path="/writing" element={<StudentLesson type="writing" />} />
                                        <Route path="/progress" element={<StudentProgress />} />
                                        <Route path="/achievements" element={<MobileAchievementsWrapper />} />
                                        <Route path="/profile" element={<MobileProfileWrapper />} />
                                        <Route path="/profile/settings" element={<MobileProfileSettings />} />
                                        <Route path="/profile/settings/personal" element={<MobilePersonalInfo />} />
                                        <Route
                                            path="/profile/settings/learning"
                                            element={<MobileLearningObjectives />}
                                        />
                                        <Route path="/exercise" element={<StudentExercise />} />
                                        <Route path="/exercise-speaking" element={<StudentExerciseSpeaking />} />
                                        {/* @ts-expect-error - WritingPracticeInterface component type issue */}
                                        <Route path="/writing-session" element={<WritingPracticeInterface />} />
                                        <Route path="/assessments" element={<Assessments />} />

                                        {/* Skill-specific learning routes within layout */}
                                        <Route path="/learning/testing" element={<StudentLearning />} />
                                        <Route path="/learning/reading" element={<StudentLearning />} />
                                        <Route path="/learning/speaking" element={<StudentLearning />} />
                                        <Route path="/learning/vocabulary" element={<StudentLearning />} />
                                        <Route path="/learning/writing" element={<StudentLearning />} />
                                        <Route path="/learning/listening" element={<StudentLearning />} />
                                        <Route path="/learning/grammar" element={<StudentLearning />} />
                                        <Route path="/learning/preview" element={<StudentLearning />} />
                                        <Route
                                            path="/reading/passage/:passageId/practice"
                                            element={<ReadingPracticeInterface />}
                                        />
                                        <Route
                                            path="/reading/session/:sessionId/practice"
                                            element={<ReadingPracticeInterface />}
                                        />
                                        <Route path="/" element={<StudentDashboard />} />
                                        {/* No result page exists; this path always showed the dashboard */}
                                        <Route
                                            path="/exercise-complete"
                                            element={<Navigate to="/student/dashboard" replace />}
                                        />
                                        {/* Unknown paths (e.g. /student/notifications) used to render the dashboard */}
                                        <Route path="*" element={<NotFoundPage />} />
                                    </Routes>
                                </StudentLayout>
                            }
                        />
                    </Routes>
                </Layout>
            </ProtectedRoutes>
        </>
    );
};

export default StudentRoutes;
